import type { Entry } from 'contentful'
import { type ComponentPropsWithRef, type ReactElement } from 'react'

import { type ctaArgType, useGetAuthAndDeviceTypeCta } from '@lib/hooks/auth-and-device-type-cta'
import { useUserService } from '@lib/hooks/services'
import { createLinkEntry } from '@lib/utils/create-link-entry'

import { isUnresolvedLink } from '@contentful-types/content-types'
import { PageHeaderType, type TypeHeaderSkeleton } from '@contentful-types/header'

import { getConfig } from '@config/index'

import { CompactCtaContext } from '@contexts/compact-cta-context'

import { SigninCtaButton, SignupCtaButton } from '@components/cta/cta-button/auth/auth'
import { CtaButton } from '@components/cta/cta-button/cta-button'
import { LinkButton } from '@components/cta/cta-button/link/link'
import { Header, HeaderType } from '@components/ui-core/header/header'

import { headerContent } from './content'

const config = getConfig()
const cmsHeaderTypeToPageHeaderType: Record<PageHeaderType, HeaderType> = {
  [PageHeaderType.NoNavigation]: HeaderType.NoNavigation,
  [PageHeaderType.Global]: HeaderType.Global,
}

// Smart CTA component that handles authentication state
const SmartDefaultCtas = () => {
  const { isAnonymous } = useUserService().currentUser

  // TODO: localize CTAs / extract content
  return isAnonymous ? (
    <>
      {/* data-nav-link is used to style the link as a navigation link in the header when unstyled */}
      <SigninCtaButton isCompact={true} text="Log in" category="unstyled" data-nav-link />
      <SignupCtaButton isCompact={true} text="Sign up" category="primary" />
    </>
  ) : (
    <>
      <LinkButton
        isCompact={true}
        text="My Superhuman"
        category="unstyled"
        targetLink={createLinkEntry(`${config.paths.nomos.basePath}${config.paths.nomos.atc}`, {
          title: 'My Superhuman',
          internalName: 'My Superhuman',
        })}
        /* data-nav-link is used to style the link as a navigation link in the header when unstyled */
        data-nav-link
      />
      <LinkButton
        isCompact={true}
        text="Upgrade"
        category="primary"
        targetLink={createLinkEntry(`${config.paths.basePath}${config.paths.plans}`, {
          title: 'Upgrade',
          internalName: 'Header Upgrade CTA',
        })}
      />
    </>
  )
}

export interface PageHeaderProps extends ComponentPropsWithRef<'header'> {
  header: Entry<TypeHeaderSkeleton> | undefined
}

export function PageHeader({ header, ...rest }: PageHeaderProps): ReactElement {
  const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()
  const variationContainerOrCta = (header?.fields.stickyCtas ?? []) as ctaArgType[]
  const ctaContent = variationContainerOrCta
    .map(variationContainerOrCta => getAuthAndDeviceTypeCta(variationContainerOrCta as ctaArgType))
    .filter(cta => !isUnresolvedLink(cta))
    .map(cta => <CtaButton key={cta.sys.id} data={cta} />)

  return (
    <CompactCtaContext.Provider value={true}>
      <Header
        navLinks={headerContent}
        ctaContent={<>{ctaContent}</>}
        type={
          cmsHeaderTypeToPageHeaderType[
            (header?.fields.type as PageHeaderType) ?? PageHeaderType.Global
          ]
        }
        {...rest}
      />
    </CompactCtaContext.Provider>
  )
}

export interface DefaultPageHeader extends ComponentPropsWithRef<'header'> {
  type: HeaderType
}

export function DefaultPageHeader({ type, ...rest }: DefaultPageHeader): ReactElement {
  const isGlobal = type === HeaderType.Global
  return (
    <CompactCtaContext.Provider value={true}>
      <Header
        navLinks={isGlobal ? headerContent : []}
        ctaContent={<SmartDefaultCtas />}
        type={type}
        {...rest}
      />
    </CompactCtaContext.Provider>
  )
}
